/**
 * RELATIONSHIP TREE TYPES
 * Defines the data structure for hierarchical relationship trees.
 * Used for any hierarchical relationship structure (contractor relationships, org charts, etc).
 */

import type { Timestamp } from 'firebase/firestore';

/**
 * Extra information that can be attached to tree nodes.
 * Notes are stored as an array with user attribution and timestamps.
 */
export type NodeExtraInformation = {
    /** Array of notes with user attribution and timestamps */
    notes?: Array<{
        /** The note text content */
        text: string;
        /** Display name of the user who created the note */
        userName: string;
        /** Firestore timestamp when the note was created */
        timestamp: Timestamp;
    }>;
};

/** Type of relationship tree (determines behavior and UI) */
export type RelationshipTreeType = 'contractor' | 'specifier' | 'recipient' | 'other';

/** Email configuration for recipient tree nodes */
export type RecipientEmailConfig = {
    /** Email role (to, cc, or bcc) */
    role: 'to' | 'cc' | 'bcc';
    /** Whether this recipient is enabled */
    enabled: boolean;
};

/**
 * Core data structure for a relationship tree node.
 * This is the pure data type stored in the database.
 * Uses a single entityReference to track the selected record for this node.
 */
type RelationshipTreeNodeBase = {
    /** Unique identifier for the tree node (UUID generated at creation) */
    key: string;
    /** Discriminant for what kind of tree this node belongs to */
    treeType: RelationshipTreeType;
    /** Entity or entity group ID this node represents (defines what type of entity can be selected) */
    entityOrGroupId: string;
    /** The referenced entity record ID for this node */
    entityRecordId?: string;
    /** Child nodes in the hierarchy */
    children?: RelationshipTreeNode[];
    /** Extra information attached to this node */
    extraInformation?: NodeExtraInformation;
    /** Whether this node is locked from editing */
    isLocked?: boolean;
    /** Whether this is a leaf node (cannot have children) */
    isLeaf?: boolean;
};

/**
 * Relationship tree node with discriminated union based on tree type.
 * Recipient trees have recipientConfig; other types do not.
 */
export type RelationshipTreeNode =
    | (RelationshipTreeNodeBase & {
        treeType: 'recipient';
        /** Recipient configuration for recipient trees (separate from notes) */
        recipientConfig?: RecipientEmailConfig;
    })
    | (RelationshipTreeNodeBase & {
        treeType: Exclude<RelationshipTreeType, 'recipient'>;
        recipientConfig?: never;
    });

/** Extract type for recipient tree nodes (nodes with recipient configuration) */
export type RelationshipTreeContactNode = Extract<RelationshipTreeNode, { treeType: 'recipient' }>;

/**
 * Complete relationship tree value structure.
 * This is what gets stored in the field value.
 */
export type RelationshipTreeValue = {
    /** Array of root-level trees */
    trees: RelationshipTreeNode[];
    /**
     * Array of winning entity record IDs in hierarchical order.
     * `[]` explicitly means "no winning combination"; `undefined` only for
     * legacy persisted values that pre-date the field being written.
     */
    winningRecordIds?: string[];
};

/**
 * Configuration for entity types in the relationship tree.
 *
 * Discriminated by `kind`:
 *  - `'entityGroup'`: filtered reference to records within an entity group
 *    (e.g. "Customers in the GENERAL_CONTRACTORS group"). Requires both
 *    `entityId` and `entityGroupId`.
 *  - `'entity'`: direct reference to records of an entity, no group filter
 *    (e.g. "any Contact", "any Distributor"). Carries only `entityId`.
 */
type RelationshipTreeEntityConfigBase = {
    /** Display label for this entity type */
    label: string;
    /** Short abbreviation for this entity type (e.g., 'GC', 'EC', 'CUST') */
    abbreviation: string;
    /** Color for visual distinction (e.g., 'blue', 'orange', 'green') */
    color: string;
    /** Entity IDs that can be children of this type */
    allowedChildren: string[];
    /** Entity IDs that can be siblings of this type */
    allowedSiblings: string[];
    /** Whether this entity type can be marked as winning */
    supportsWinning: boolean;
    /** Label to show when marked as winning */
    winningLabel?: string;
    /** If true, requires complete path from root to have records before showing winning option */
    requiresCompletePathForWinning?: boolean;
    /** If true, validates that combinations are unique across the tree */
    validateUniqueCombinations?: boolean;
    /** Default extra information structure for new nodes */
    extraDataTemplate: NodeExtraInformation;
    /** Default recipient config for new nodes (recipient trees only) */
    defaultRecipientConfig?: RecipientEmailConfig;
    /**
     * When true, this entry is an operation type rather than a selectable entity record.
     * Its `entityId` is `OPERATION_TYPE_ENTITY_ID` and its entry key is a form type ID.
     */
    isOperationType?: boolean;
};

export type RelationshipTreeEntityConfig =
    | (RelationshipTreeEntityConfigBase & {
        kind: 'entityGroup';
        /** The Firestore entity collection ID the group belongs to */
        entityId: string;
        /** The entity group ID used to filter records within the entity */
        entityGroupId: string;
    })
    | (RelationshipTreeEntityConfigBase & {
        kind: 'entity';
        /** The Firestore entity collection ID this node references directly */
        entityId: string;
        entityGroupId?: never;
    });

/**
 * Complete tree configuration type.
 * Defines the structure and rules for a relationship tree.
 */
export type RelationshipTreeConfig = {
    /** The type of relationship tree this config represents */
    treeType: RelationshipTreeType;
    /** Configuration for each entity type */
    entityConfig: Record<string, RelationshipTreeEntityConfig>;
    /** Maximum depth of the tree */
    maxDepth: number;
    /** Root entity ID for initial tree structure */
    rootEntityId: string;
};

/**
 * `entityId` of an operation-type entry. It names no `entities/` document: the entry key
 * is a form type ID, which the send action's `operationTypeId` matches against a root
 * node's `entityOrGroupId`, so `entityId` only marks the entry as non-selectable.
 */
export const OPERATION_TYPE_ENTITY_ID = '_operationType';

/** Empty/default RelationshipTreeConfig — used when creating a new relationshipTree field. */
export const EMPTY_RELATIONSHIP_TREE_CONFIG: RelationshipTreeConfig = {
    treeType: 'other',
    rootEntityId: '',
    maxDepth: 3,
    entityConfig: {},
};